/* Shared pieces of the home thread list. Two components show the user's
   global coordinator threads: the Sidebar's five most recent, and the full
   Threads scene (/threads) with search. This module holds what they agree on.

   Sync: the two lists are separate components with separate fetches, so a
   rename, delete or file-to-project in one would leave the other stale until
   the next navigation. Whichever one made the change fires
   THREADS_CHANGED_EVENT once its write has landed, and both listen and
   refetch. A window event, the same decoupling the Sidebar already uses for
   'daedalus:open-artifact'. */

import type { ProjectRow } from '@/api/client';
import { etDate, etParts } from '@/lib/datetime';
import type { AssignProject } from './ThreadRowMenu';

/** How many threads the Sidebar lists before "All threads". */
export const RECENT_THREADS_LIMIT = 5;

/** How many threads the Threads scene asks for: the server's cap
 *  (ListConversationsRequest.limit, le=200). list-conversations takes a
 *  limit but no offset, so this is a ceiling, not a page size. 50 was too
 *  low — a heavy user already had 72 home threads on 2026-10-07, and 200
 *  came back in ~0.3s. */
export const THREADS_PAGE_LIMIT = 200;

export const THREADS_CHANGED_EVENT = 'daedalus:threads-changed';

export function notifyThreadsChanged(): void {
  window.dispatchEvent(new CustomEvent(THREADS_CHANGED_EVENT));
}

/** Epoch ms for a conversation timestamp, or NaN.
 *
 *  user_conversations.last_active_at is a DATETIME2 written with
 *  GETUTCDATE(), and Python's isoformat() serializes it with no offset:
 *  "2026-10-07T14:00:00.123000". Date.parse reads an offset-less date-time
 *  as LOCAL time, which on an Eastern browser makes every thread 4-5 hours
 *  younger than it is. The value is UTC, so read it as UTC. A string that
 *  already carries Z or an offset is left alone. */
export function parseUtcStamp(iso: string | null | undefined): number {
  if (!iso) return NaN;
  const s = iso.trim();
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  return Date.parse(hasZone || isDateOnly ? s : `${s}Z`);
}

/** "just now" / "12m ago" / "3h ago" / "yesterday" / "4d ago", then a
 *  short Eastern date ("Sep 29"), with the year once it isn't this year. */
export function threadWhen(iso: string | null | undefined, now: number = Date.now()): string {
  const then = parseUtcStamp(iso);
  if (Number.isNaN(then)) return '';
  const min = Math.floor((now - then) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 2) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  const sameYear = etParts(then)?.year === etParts(now)?.year;
  return (
    etDate(
      then,
      sameYear
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' }
    ) ?? ''
  );
}

/** The ThreadRowMenu's Add-to-project choices from listMyProjects rows:
 *  one per project id, skipping rows with no usable id or name. The rows
 *  are access-scoped server-side, so whatever comes back is safe to offer. */
export function toAssignProjects(rows: ProjectRow[]): AssignProject[] {
  const seen = new Set<number>();
  const opts: AssignProject[] = [];
  for (const r of rows) {
    const id = Number(r.project_id ?? r.id);
    const rawName = r.project_name ?? r.name;
    if (!Number.isFinite(id) || !rawName || seen.has(id)) continue;
    seen.add(id);
    opts.push({ id, name: String(rawName) });
  }
  return opts;
}

/** The line above the list: how many threads, or how many matched. At the
 *  ceiling the count is not the total, so it says "latest" / "first". */
export function threadCountLabel(
  n: number,
  query: string,
  limit: number = THREADS_PAGE_LIMIT
): string {
  const q = query.trim();
  if (q) {
    if (n >= limit) return `First ${limit} matches for “${q}”`;
    return n === 1 ? `1 match for “${q}”` : `${n} matches for “${q}”`;
  }
  if (n >= limit) return `Your latest ${limit} threads`;
  return n === 1 ? '1 thread' : `${n} threads`;
}
